import { useEffect, useState } from "react";
import { Heading } from "../components/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { collection, onSnapshot, query, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import EmptyState from "./components/empty-state";
import { PlusIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { formatDate } from "../helpers/date.helpers";
import { formatCurrency } from "../helpers/currency.helpers";
import { Input } from "../components/input";
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from "../components/dropdown";
import { BSBillItem } from "@/models/billItem";
import { BSCustomer } from "@/models/customer";
import { BSHorse } from "@/models/horse";
import { BSService } from "@/models/service";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { BSParams } from "@/models/params";
import { doc, getDoc } from "firebase/firestore";
import { Button } from "../components/button";
import { SwitchField } from "../components/switch";
import { Label } from "../components/fieldset";
import { Switch } from "../components/switch";

export default function Billing() {
  // Data
  const [billItems, setBillItems] = useState<BSBillItem[]>([]);
  const [customers, setCustomers] = useState<BSCustomer[]>([]);
  const [horses, setHorses] = useState<BSHorse[]>([]);
  const [services, setServices] = useState<BSService[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [hideExported, setHideExported] = useState(true);

  // Columns configuration
  const columns: ColumnConfig<BSBillItem>[] = [
    { field: 'date', label: 'Date', defaultVisible: true },
    { field: 'customerId', label: 'Customer', defaultVisible: true },
    { field: 'horseId', label: 'Horse', defaultVisible: true },
    { field: 'serviceId', label: 'Service', defaultVisible: true },
    { field: 'quantity', label: 'Quantity', defaultVisible: true },
    { field: 'value', label: 'Value', defaultVisible: true },
    { field: 'location', label: 'Location', defaultVisible: true },
    { field: 'comments', label: 'Comments', defaultVisible: false },
  ];

  // Add hooks
  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSBillItem>('date');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSBillItem>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSBillItem>();

  // Fetch data
  useEffect(() => {
    const fetchBillItems = () => {
      const q = query(collection(db, "billItems"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSBillItem[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as BSBillItem;
          const date = data.date instanceof Timestamp
            ? data.date.toDate()
            : data.date;
          items.push({ ...data, id: doc.id, date });
        });
        setBillItems(items);
      });
    };

    const fetchCustomers = () => {
      const q = query(collection(db, "customers"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSCustomer[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSCustomer, id: doc.id });
        });
        setCustomers(items);
      });
    };

    const fetchHorses = () => {
      const q = query(collection(db, "horses"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSHorse[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSHorse, id: doc.id });
        });
        setHorses(items);
      });
    };

    const fetchServices = () => {
      const q = query(collection(db, "services"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSService[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSService, id: doc.id });
        });
        setServices(items);
      });
    };

    fetchBillItems();
    fetchCustomers();
    fetchHorses();
    fetchServices();
  }, []);

  // Add SortableHeader component
  const SortableHeader = ({ field, children }: { field: keyof BSBillItem, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  // Add filtered data function
  const getFilteredBillItems = () => {
    let filtered = billItems;
    
    // Filter out exported items if hideExported is true
    if (hideExported) {
      filtered = filtered.filter(item => !item.exported);
    }

    // Apply search filter
    filtered = getFilteredData(filtered, (item) => [
      formatDate(item.date) || '',
      customers.find(c => c.id === item.customerId)?.name || '',
      horses.find(h => h.id === item.horseId)?.name || '',
      services.find(s => s.id === item.serviceId)?.productServiceName || '',
      item.quantity.toString(),
      formatCurrency(item.value),
      item.location,
      item.comments || ''
    ]);

    return getSortedData(filtered);
  };

  const exportToQB = async () => {
    setIsExporting(true);
    try {
      // Get the invoice number from params
      const paramsRef = doc(db, "params", "B1XduGleWvW0eXKqZ743");
      const paramsSnap = await getDoc(paramsRef);
      let nextInvoiceNumber = (paramsSnap.data() as BSParams).invoiceNumber;

      // Group bill items by horse to ensure same invoice number
      const itemsByHorse = getFilteredBillItems().reduce((acc, item) => {
        const key = item.horseId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {} as Record<string, BSBillItem[]>);

      // Prepare CSV data
      const csvRows = ['InvoiceNo,InvoiceDate,DueDate,Terms,Customer,ServiceDate,Item(Product/Service),ItemDescription,ItemQuantity,ItemRate,Class,ItemAmount'];

      Object.values(itemsByHorse).forEach(horseItems => {
        horseItems.forEach(item => {
          const invoiceDate = new Date(item.date);
          const dueDate = new Date(item.date);
          dueDate.setDate(dueDate.getDate() + 15);

          const customer = customers.find(c => c.id === item.customerId)?.name || 'Unknown';
          const service = services.find(s => s.id === item.serviceId)?.productServiceName || 'Unknown';
          const horse = horses.find(h => h.id === item.horseId)?.name || 'Unknown';
          const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(item.date);
          const location = item.location.toUpperCase().replace('-', ' ');
          const itemAmount = item.quantity * item.value;

          const row = [
            nextInvoiceNumber,
            invoiceDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            dueDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            'Net 15',
            customer,
            invoiceDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            service,
            `${service} for ${horse}-${month}`,
            item.quantity,
            item.value,
            location,
            itemAmount
          ].join(',');

          csvRows.push(row);
        });
        nextInvoiceNumber++; // Increment for next horse
      });

      // Create and download CSV
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `quickbooks_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Update the params document with the new invoice number
      await updateDoc(paramsRef, {
        invoiceNumber: nextInvoiceNumber
      });

      // Mark all exported items as exported
      const updatePromises = getFilteredBillItems().map(item => 
        updateDoc(doc(db, 'billItems', item.id), { exported: true })
      );
      await Promise.all(updatePromises);

    } catch (error) {
      console.error('Error exporting to QB:', error);
      // TODO: Add error handling
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Billing</Heading>
        <div className="flex items-center gap-4">
          <SwitchField>
            <Label>Showing {hideExported ? 'pending bills' : 'all bills'}</Label>
            <Switch color="green" checked={hideExported} onChange={setHideExported} />
          </SwitchField>
          <div className="relative">
            <Input
              type="search"
              placeholder="Search bills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={exportToQB}
            disabled={isExporting || billItems.length === 0}
          >
            {isExporting ? (
              <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Exporting...
              </>
            ) : (
              'Export to QB'
            )}
          </Button>
        </div>
      </div>

      {billItems.length > 0 ? (
        <Table
          dense
          striped
          className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
        >
          <TableHead>
            <TableRow>
              {columns.map(({ field, label }) => 
                isColumnVisible(field) && (
                  <SortableHeader key={field} field={field}>
                    {label}
                  </SortableHeader>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredBillItems().map((item) => (
              <TableRow key={item.id}>
                {isColumnVisible('date') && (
                  <TableCell>{formatDate(item.date)}</TableCell>
                )}
                {isColumnVisible('customerId') && (
                  <TableCell>
                    {customers.find(c => c.id === item.customerId)?.name || 'Unknown'}
                  </TableCell>
                )}
                {isColumnVisible('horseId') && (
                  <TableCell>
                    {horses.find(h => h.id === item.horseId)?.name || 'Unknown'}
                  </TableCell>
                )}
                {isColumnVisible('serviceId') && (
                  <TableCell>
                    {services.find(s => s.id === item.serviceId)?.productServiceName || 'Unknown'}
                  </TableCell>
                )}
                {isColumnVisible('quantity') && (
                  <TableCell>{item.quantity}</TableCell>
                )}
                {isColumnVisible('value') && (
                  <TableCell>{formatCurrency(item.value)}</TableCell>
                )}
                {isColumnVisible('location') && (
                  <TableCell>{item.location === 'florida' ? 'Florida' : 'Long Island'}</TableCell>
                )}
                {isColumnVisible('comments') && (
                  <TableCell>{item.comments}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title="No Bills"
          description="No bills have been generated yet"
        >
          <PlusIcon className="w-10 h-10" />
        </EmptyState>
      )}
    </div>
  );
}
